import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { FIELD_NAMES } from "../../../../../../constants/commonConstants";
import {
  EXTRA_REPAYMENTS_ROUTE,
  MANAGE_REPAYMENTS_INCREASE_ROUTE,
  MANAGE_ARREARS_ROUTE,
} from "../../route.constants";
import { PRIVATE_ROUTE } from "../../../../../../route.constants";
import { useAccount } from "../../../../../../hooks/client";
import { MoneyInput } from "../../../../../Forms/components/fields";
import { getCreatePaymentPromiseIncreaseEndpoint } from "../../../../../../services/apiRoutes";
import { withLoanForm } from "../../../../../../components/fairGoForm/withLoanForm";
import { ManageRepaymentForm } from "../../../../components/manageRepaymentForm";
import { DatePickerManageRepayment } from "./datePickerManageRepayments";
import { Wrapper } from "../loanFormsWithDatePicker.styles";
import { Wrapper as ModalWrapper } from "../../../../components/manageMyRepaymentModal/manageMyRepaymentModal.styles";
import actions from "../../../../../../redux/manageRepaymentsForm/actions";
import {
  addDaysAfterToday,
  addBusinessDaysAfterToday,
  getValidDates,
  createWorkDaySelectOptions,
  createRequiredDateDropdownShape,
} from "./utils/dateHelpersManageRepayments";
import { useCheckIfFormIsActive } from '../../../../hooks';

const ModalMessage = () => {
  return (
    <ModalWrapper>
      <p>Thank you. Your request has been received.</p>
    </ModalWrapper>
  );
};

const validDateTimes = getValidDates(
  addBusinessDaysAfterToday(2),
  addDaysAfterToday(90)
);
const filteredDates = createWorkDaySelectOptions(validDateTimes);

const INITIAL_FORM_STATE = {
  [FIELD_NAMES.PROMISE_VALUE]: "",
  [FIELD_NAMES.PROMISE_FIRST_DATE]: { day: "", month: "", year: "" },
  "__clientsideonly__promiseFirstDate": "",
};

const FORM_VALIDATION = Yup.object().shape({
  [FIELD_NAMES.PROMISE_VALUE]: Yup.string()
    .trim()
    .required("Field is required")
    .when([FIELD_NAMES.NETBALANCE_VALUE], (netbalance, schema) => {
      return schema.test({
        test: (paymentValue) => {
          return paymentValue >= 1 && paymentValue <= netbalance;
        },
        message: `Value must be between $1 and $${netbalance}`,
      });
    }),

  [FIELD_NAMES.PROMISE_FIRST_DATE]: Yup.object()
    .required("Please select a date")
    .shape(createRequiredDateDropdownShape()),
});

const _PayDirectDebit = ({ accountId }) => {
  const dispatch = useDispatch();
  const { netBalance } = useAccount(accountId) || {};
  const { setFieldValue, isValid } = useFormikContext();

  useEffect(() => {
    setFieldValue(FIELD_NAMES.NETBALANCE_VALUE, netBalance);
    if (isValid === true) {
      dispatch(actions.markFormAsValid());
    } else {
      dispatch(actions.markFormAsInvalid());
    }
  }, [netBalance, setFieldValue, isValid, dispatch]);

  return (
    <div className="input-wrapper">
      <MoneyInput
        className="pay-higher-input"
        decimalPlaces={2}
        name={FIELD_NAMES.PROMISE_VALUE}
        title="How much will you pay?"
        label="amount"
      />
      <DatePickerManageRepayment
        title="When will you pay?"
        className="start-date-label"
        name={FIELD_NAMES.PROMISE_FIRST_DATE}
        options={filteredDates}
        placeholder="Select Date"
      />
    </div>
  );
};

const PayDirectDebitForm = withLoanForm(_PayDirectDebit);

export const PayDirectDebit = ({ accountId }) => {
  const { isFormValidated } = useSelector(
    (state) => state.manageRepaymentsForm
  );

  const matchIncreaseRepayment =
    PRIVATE_ROUTE.MANAGE_REPAYMENTS +
    MANAGE_REPAYMENTS_INCREASE_ROUTE.INCREASE +
    EXTRA_REPAYMENTS_ROUTE.DIRECT_DEBIT;

  const matchManageArrears = 
    PRIVATE_ROUTE.MANAGE_REPAYMENTS + MANAGE_ARREARS_ROUTE.ARREARS + EXTRA_REPAYMENTS_ROUTE.DIRECT_DEBIT;

  const isIncreaseRepaymentsActive = useCheckIfFormIsActive(matchIncreaseRepayment);
  const isManageArrearsActive = useCheckIfFormIsActive(matchManageArrears);
  const isActive = isIncreaseRepaymentsActive || isManageArrearsActive;

  const buttonText = isFormValidated
    ? "I Agree And Confirm My Request"
    : "Continue"; //if we remove isFormValidated redux state, we can use something like const [buttonText, setButtonText] = useState("CONTINUE"); instead to set the button text

  return (
    <Wrapper>
      <ManageRepaymentForm
        buttonText={buttonText}
        endpoint={getCreatePaymentPromiseIncreaseEndpoint(accountId)}
        initialValues={{ ...INITIAL_FORM_STATE }}
        validationSchema={FORM_VALIDATION}
        modalMessage={<ModalMessage />}
        isActive={isActive}
      >
        <PayDirectDebitForm accountId={accountId} />
      </ManageRepaymentForm>
    </Wrapper>
  );
};
